import { createComputed, createEffect, createMemo, createResource, For, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { useLanguage } from "@/context/language"
import { formatDateTime } from "@/utils/date"
import { listReportShares, requestApprovals, shareReport, type CoworkArtifact } from "@/cowork/api"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { DialogBody, DialogHeader, DialogTitle, DialogV2 } from "@opencode-ai/ui/v2/dialog-v2"
import { approvalRecipients } from "@/cowork/approval-recipients"
import { reportDisplayTitle, reportFormatLabel } from "@/cowork/report-preview"
import "@/cowork/report-share.css"

export function ApprovalRequestForm(props: {
  artifacts: Pick<CoworkArtifact, "id" | "title">[]
  allowSharing?: boolean
  assignedEmails?: string[]
  hideReportPicker?: boolean
  presentation?: "dialog"
  onCancel?: () => void
  onRequest: (artifactID: string, emails: string[], message: string | undefined, purpose: "share" | "approval") => Promise<void>
}) {
  const language = useLanguage()
  let disposed = false
  onCleanup(() => {
    disposed = true
  })
  const [store, setStore] = createStore({
    artifactID: "", emails: "", message: "", requesting: false, error: "", emailTouched: false,
    purpose: (props.allowSharing ? "share" : "approval") as "share" | "approval",
  })
  const sharing = () => store.purpose === "share"
  const recipients = createMemo(() => approvalRecipients(store.emails, sharing() ? [] : props.assignedEmails))
  const selectedArtifact = createMemo(
    () => props.artifacts.find((artifact) => artifact.id === store.artifactID) ?? props.artifacts[0],
  )
  createEffect(() => {
    const artifact = selectedArtifact()
    if (artifact && artifact.id !== store.artifactID) setStore("artifactID", artifact.id)
  })
  const submitRequest = async (event: SubmitEvent) => {
    event.preventDefault()
    if (disposed || store.requesting) return
    const artifact = selectedArtifact()
    const emails = recipients().emails
    setStore("emailTouched", true)
    if (!artifact || !emails.length || recipients().invalid.length) return
    setStore({ requesting: true, error: "" })
    try {
      await props.onRequest(artifact.id, emails, store.message.trim() || undefined, store.purpose)
      if (!disposed) setStore({ emails: "", message: "", emailTouched: false })
    } catch (error) {
      if (!disposed) setStore("error", error instanceof Error ? error.message : String(error))
    } finally {
      if (!disposed) setStore("requesting", false)
    }
  }
  return (
    <form onSubmit={submitRequest} classList={{ "report-share-form": props.presentation === "dialog" }}>
      <Show when={props.presentation !== "dialog"}>
      <div class="mb-4">
        <h3 class="text-15-medium text-v2-text-text-base">
          {language.t(sharing() ? "cowork.report.share.title" : "cowork.approval.request.title")}
        </h3>
        <p class="mt-1 text-12-regular text-v2-text-text-muted">
          {language.t(sharing() ? "cowork.report.share.description" : "cowork.approval.request.description")}
        </p>
      </div>
      </Show>
      <Show when={props.allowSharing}>
        <fieldset class="mb-4" disabled={store.requesting}>
          <legend class="mb-2 text-12-medium text-v2-text-text-muted">{language.t("cowork.report.share.purpose")}</legend>
          <div class="report-share-purpose-options flex flex-wrap gap-4">
            <For each={["share", "approval"] as const}>
              {(purpose) => (
                <label class="report-share-purpose flex items-center gap-2 text-13-regular" data-selected={store.purpose === purpose}>
                  <input type="radio" name="report-share-purpose" value={purpose} checked={store.purpose === purpose}
                    onChange={() => setStore({ purpose, error: "" })} />
                   <span><span class="block font-medium">{language.t(purpose === "share" ? "cowork.report.share.title" : "cowork.report.share.requestApproval")}</span>
                     <Show when={props.presentation === "dialog"}><span class="mt-1 block text-12-regular leading-relaxed text-v2-text-text-muted">{language.t(purpose === "share" ? "cowork.report.share.description" : "cowork.approval.request.description")}</span></Show>
                   </span>
                </label>
              )}
            </For>
          </div>
        </fieldset>
      </Show>
      <div class="grid gap-3 lg:grid-cols-[1fr_1.2fr]">
        <Show when={!props.hideReportPicker}>
        <label class="flex flex-col gap-2">
          <span class="text-12-medium text-v2-text-text-muted">{language.t("cowork.approval.request.report")}</span>
          <select
            class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-2 text-13-regular text-v2-text-text-base outline-none"
            value={store.artifactID}
            disabled={store.requesting || props.artifacts.length <= 1}
            onChange={(event) => setStore("artifactID", event.currentTarget.value)}
          >
            <For each={props.artifacts}>{(artifact) => <option value={artifact.id}>{reportDisplayTitle(artifact.title)}</option>}</For>
          </select>
        </label>
        </Show>
        <label class="flex flex-col gap-2">
          <span class="text-12-medium text-v2-text-text-muted">
            {language.t(sharing() ? "cowork.report.share.recipients" : "cowork.approval.request.reviewers")}
          </span>
          <textarea
            name="recipients"
            rows={3}
            inputmode="email"
            spellcheck={false}
            autocapitalize="off"
            class="min-h-20 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-2 py-2 text-13-regular text-v2-text-text-base outline-none"
            value={store.emails}
            disabled={store.requesting}
            placeholder={language.t(sharing() ? "cowork.report.share.recipientsPlaceholder" : "cowork.approval.request.reviewers.placeholder")}
            onInput={(event) => setStore("emails", event.currentTarget.value)}
            onBlur={() => setStore("emailTouched", true)}
            aria-invalid={store.emailTouched && !!recipients().invalid.length}
          />
          <span class="text-12-regular text-v2-text-text-muted">{language.t("cowork.approval.workspace.emailHint")}</span>
          <Show when={store.emailTouched && recipients().invalid.length}><span role="alert" class="text-12-regular text-v2-state-fg-danger">{language.t("cowork.approval.workspace.invalidEmails", { emails: recipients().invalid.join(", ") })}</span></Show>
          <Show when={recipients().assigned.length}><span class="text-12-regular text-v2-text-text-muted">{language.t("cowork.approval.workspace.assigned")}</span></Show>
        </label>
      </div>
      <label class="mt-3 flex flex-col gap-2">
        <span class="text-12-medium text-v2-text-text-muted">{language.t("cowork.approval.request.message")}</span>
        <textarea
          name="message"
          rows={3}
          class="min-h-20 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-2 py-2 text-13-regular text-v2-text-text-base outline-none"
          value={store.message}
          disabled={store.requesting}
            placeholder={language.t(sharing() ? "cowork.report.share.messagePlaceholder" : "cowork.approval.request.message.placeholder")}
          onInput={(event) => setStore("message", event.currentTarget.value)}
        />
      </label>
      <Show when={store.error}>
        <p role="alert" class="mt-3 text-12-regular text-v2-state-fg-danger">
          {store.error}
        </p>
      </Show>
      <div class="report-share-footer mt-4 flex justify-end gap-2">
        <Show when={props.onCancel}><button type="button" class="report-workspace-action" disabled={store.requesting} onClick={() => props.onCancel?.()}>{language.t("common.cancel")}</button></Show>
        <button
          type="submit"
          class="rounded-lg bg-v2-background-bg-contrast px-4 py-2.5 text-13-medium text-v2-text-text-contrast hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!selectedArtifact() || !recipients().emails.length || !!recipients().invalid.length || store.requesting}
        >
          {language.t(store.requesting ? "cowork.approval.request.sending" : sharing() ? "cowork.report.share.send" : props.allowSharing ? "cowork.report.share.requestApproval" : "cowork.approval.requestReview")}
        </button>
      </div>
    </form>
  )
}

export function ReportShareDialog(props: { artifact: CoworkArtifact; active: () => boolean }) {
  const language = useLanguage()
  const dialog = useDialog()
  const artifact = props.artifact
  const [state, setState] = createStore({ sent: false })
  const [history, historyActions] = createResource(() => listReportShares(artifact.project_id, artifact.id, artifact.company_id))
  let disposed = false
  onCleanup(() => {
    disposed = true
  })
  createComputed(() => {
    if (!props.active()) dialog.close()
  })
  return (
    <DialogV2 fit containerClass="report-share-dialog !h-auto max-h-[calc(100dvh_-_24px)] !w-[min(calc(100vw_-_24px),560px)]">
      <DialogHeader closeLabel={language.t("common.close")}>
        <DialogTitle>{language.t("cowork.enterprise.reports.share")}</DialogTitle>
      </DialogHeader>
      <DialogBody class="min-h-0 overflow-y-auto p-5">
        <div class="report-share-summary" aria-label={language.t("cowork.approval.request.report")}>
          <span class="report-share-summary-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 3H5v18h14V8zM14 3v5h5M8 12h8M8 16h8" /></svg></span>
          <div class="min-w-0"><p class="break-words text-14-medium">{reportDisplayTitle(artifact.title)}</p><p class="mt-1 text-12-regular text-v2-text-text-muted">{reportFormatLabel(artifact.format, language.t("cowork.pipeline.report"))}</p></div>
        </div>
        <ApprovalRequestForm
          artifacts={[artifact]}
          allowSharing
          hideReportPicker
          presentation="dialog"
          onCancel={() => dialog.close()}
          onRequest={async (artifactID, emails, message, purpose) => {
            if (disposed || !props.active()) return
            setState("sent", false)
            if (purpose === "approval") {
              await requestApprovals(artifact.project_id, artifactID, emails, message, artifact.company_id)
              if (!disposed && props.active()) dialog.close()
              return
            }
            const result = await shareReport(artifact.project_id, artifactID, emails, message, artifact.company_id)
            if (disposed || !props.active()) return
            historyActions.mutate([...result, ...(history.error ? [] : history() ?? [])])
            void Promise.resolve(historyActions.refetch()).catch(() => undefined)
            if (result.some((item) => item.status !== "sent")) {
              throw new Error(language.t("cowork.report.share.deliveryFailed"))
            }
            setState("sent", true)
          }}
        />
        <Show when={state.sent}>
          <p role="status" class="mt-3 text-13-regular text-v2-state-fg-success">{language.t("cowork.report.share.sent")}</p>
        </Show>
        <details class="report-share-history mt-5 border-t border-v2-border-border-base pt-4" open={state.sent}>
          <summary class="cursor-pointer text-13-medium">{language.t("cowork.report.share.history")} <Show when={!history.error && history()?.length}><span class="ml-2 rounded-full bg-v2-background-bg-layer-02 px-2 py-0.5 text-12-regular">{history()?.length}</span></Show></summary>
          <Show when={!history.loading} fallback={<p class="mt-3 text-12-regular">{language.t("common.loading")}</p>}>
            <Show when={!history.error} fallback={<p role="alert" class="mt-3 text-12-regular">{language.t("cowork.report.share.historyError")}</p>}>
              <Show when={history()?.length} fallback={<p class="mt-3 text-12-regular text-v2-text-text-muted">{language.t("cowork.report.share.historyEmpty")}</p>}>
                <ul class="mt-3 max-h-48 space-y-3 overflow-y-auto text-12-regular">
                  <For each={history()}>{(item) => (
                    <li class="flex flex-wrap justify-between gap-2 rounded-lg bg-v2-background-bg-layer-01 p-3">
                      <span class="break-all">{item.recipient_email}</span>
                      <span class="text-v2-text-text-muted">
                        {language.t(`cowork.report.share.status.${item.status}`)} · {formatDateTime(item.sent_at || item.created_at)}
                      </span>
                    </li>
                  )}</For>
                </ul>
              </Show>
            </Show>
          </Show>
        </details>
      </DialogBody>
    </DialogV2>
  )
}
